import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Box, Card, Skeleton, Typography } from '@mui/material';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import { useMaxWidthTabletLarge } from 'hooks/useMediaQueries';
import type { CrowdTimestep } from 'types/crowd';
import type { GraphDataError } from 'types/graphs';

import LiveIndicator from '../LiveIndicator';
import CrowdGraph from './components/CrowdGraph/CrowdGraph';

import styles from './Crowd.module.scss';
import camInsightsStyles from '../../CameraInsights.module.scss';

const CrowdHeader: React.FC<{ isLive?: boolean }> = ({ isLive }) => {
  const isMobileView = useMaxWidthTabletLarge();

  return (
    <Box className={camInsightsStyles.header}>
      <LiveIndicator isLive={isLive} />
      <Typography
        className={camInsightsStyles.headerTitle}
        variant={isMobileView ? 'callout2' : 'subtitle1'}
      >
        Crowd
      </Typography>
    </Box>
  );
};

interface TrimmedData {
  observed: Array<CrowdTimestep | undefined> | null;
  predicted: Array<CrowdTimestep | undefined> | undefined;
}

interface CrowdContentProps {
  compareMessage?: string | null;
  data: {
    observed: Array<CrowdTimestep | undefined> | null;
    predicted: Array<CrowdTimestep | undefined> | undefined;
  };
  isLive?: boolean;
  isLoading?: boolean;
  isResizing?: boolean;
}

const CrowdContent: React.FC<CrowdContentProps> = ({
  compareMessage,
  data,
  isLive,
  isLoading,
  isResizing,
}) => {
  const trimmedData = useMemo(() => {
    const dataObject: TrimmedData = {
      observed: null,
      predicted: undefined,
    };
    if (data?.predicted) {
      const firstNonNullIndex = data.predicted.findIndex((item) => item && item?.crowd !== null);
      const lastNonNullIndex =
        data.predicted.length -
        1 -
        data.predicted
          .slice()
          .reverse()
          .findIndex((item) => item && item.crowd !== null);
      const sliceIndexStart = firstNonNullIndex > 2 ? firstNonNullIndex - 3 : 0;
      const sliceIndexEnd =
        lastNonNullIndex + 2 < data.predicted.length ? lastNonNullIndex + 2 : data.predicted.length;

      dataObject.predicted = data.predicted?.slice(sliceIndexStart, sliceIndexEnd);
      dataObject.observed = data.observed ?? null;
    }
    return dataObject;
  }, [data]);
  return (
    <Box
      className={classNames(camInsightsStyles.insightsModule, styles.crowd)}
      data-testid="camera-insights-crowd"
    >
      <Card className={camInsightsStyles.insightsModuleCard} elevation={0}>
        <CrowdHeader isLive={isLive} />
        {isLoading || isResizing ? (
          <Box data-testid="camera-insights-crowd-loading">
            <Skeleton
              animation="pulse"
              className={camInsightsStyles.loading}
              sx={{ height: '91px', margin: '8px 0' }}
              variant="rounded"
            />
            <Skeleton
              animation="pulse"
              className={camInsightsStyles.loading}
              sx={{ height: '20px', width: '60%' }}
              variant="text"
            />
          </Box>
        ) : (
          <>
            <CrowdGraph data={trimmedData} />
            <Typography
              className={styles.compareMessage}
              component="p"
              data-testid="camera-insights-crowd-message"
              variant="caption1"
            >
              {compareMessage}
            </Typography>
          </>
        )}
      </Card>
    </Box>
  );
};

interface CrowdProps extends CrowdContentProps {
  error?: string | null;
}

const Crowd: React.FC<CrowdProps> = ({
  compareMessage,
  data,
  error,
  isLive,
  isLoading,
  isResizing,
}) => (
  <ErrorBoundary
    error={!!error}
    errorBoundaryId="camera-insights-crowd"
    render={() => (
      <Box
        className={classNames(camInsightsStyles.insightsModule, styles.crowd)}
        data-testid="camera-insights-crowd"
      >
        <Card
          className={camInsightsStyles.error}
          data-testid="camera-insights-crowd-error"
          elevation={0}
        >
          <CrowdHeader isLive={isLive} />
          <ErrorMessage
            Icon={error ? null : undefined}
            className={camInsightsStyles.errorMessage}
            message={(error as GraphDataError)?.message}
          />
        </Card>
      </Box>
    )}
  >
    <CrowdContent
      compareMessage={compareMessage}
      data={data}
      isLive={isLive}
      isLoading={isLoading}
      isResizing={isResizing}
    />
  </ErrorBoundary>
);

export default Crowd;
